import * as THREE from "three";
import type { HubSpot } from "../hubs";
import type { Level } from "../level";
import type { MapLook, Mood } from "../maps";
import { buildClouds, buildEnvironment, buildSky } from "./atmosphere";
import { Dust } from "./dust";
import { buildLevelMeshes, disposeWorld } from "./level-meshes";
import { moodFor } from "./mood";
import { tickers, type Tick } from "./scenery";
import { activePasses, shadowMapSize, type ActivePasses, type Composer } from "./post";
import { activeStyle } from "./styles";

// The stage (#75): the scene's look, out of `Game`. It owns the renderer,
// the scene, the sun and sky lights, the environment map, fog, the current
// map's meshes and the post-processing chain, all from the active art style
// and the quality setting. `Game` adds its own things (rider, markers,
// pickups) to `scene` and calls `render` once a frame.

type ComposerModule = typeof import("./composer");

/** How long a failed composer load waits before trying again. */
export const COMPOSER_RETRY_MS = 5000;

export class Stage {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly sun = new THREE.DirectionalLight(0xffffff, 2.4);
  private hemi = new THREE.HemisphereLight(0xdff4ff, 0x8a7a66, 1.3);
  /** The current map's meshes (park, walls, sky dressing), swapped out on a map change. */
  private world: THREE.Group | null = null;
  /** The map's moving scenery (#156: the wheel, the carousel), moved on the render clock each frame. */
  private ticks: Tick[] = [];
  /** Environment maps: the studio one is kept; a sky one is the current map's, per style. */
  private studioEnv: THREE.Texture | null = null;
  private skyEnv: THREE.Texture | null = null;
  private quality: "low" | "high" = "high";
  /** The style's passes at this quality; null renders straight to the canvas. */
  private passes: ActivePasses | null = null;
  /** Made on the first render that needs it (it needs the camera), dropped when the passes change. */
  private composer: Composer | null = null;
  /** `render/composer.ts`, loaded on demand the first time there are passes to draw; until then frames go straight to the canvas. */
  private composerModule: ComposerModule | null = null;
  private composerLoading = false;
  /** When a failed load may try again (`performance.now()` ms). */
  private composerRetryAt = 0;
  /** The current map's mood (#179): the composer's look-up table is built for it. */
  private mood: Mood = "noon";
  /** The finisher's depth of field (#179): focus distance in metres, and how far in (0 off). */
  private focusDistance = 10;
  private focusK = 0;
  /** The style's dust in the air on High (#179), or null. */
  private dust: Dust | null = null;
  /** The last frame's `performance.now()`, for the dust's clock. */
  private lastFrame = -1;
  private width = 1;
  private height = 1;
  /** The screen's pixel ratio (capped at 2), what a style without a fixed `resolution` draws at. */
  private readonly screenRatio: number;
  /** The screen's own pixel ratio, uncapped: what a fixed `resolution` is blown up to (#146). */
  private readonly deviceRatio: number;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly importComposer: () => Promise<ComposerModule> = () => import("./composer"),
  ) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.deviceRatio = window.devicePixelRatio || 1;
    this.screenRatio = Math.min(this.deviceRatio, 2);
    this.renderer.setPixelRatio(this.screenRatio);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.scene.add(this.hemi);
    this.sun.position.set(-20, 35, 15);
    this.sun.castShadow = true;
    const sc = this.sun.shadow.camera;
    sc.left = sc.bottom = -25;
    sc.right = sc.top = 25;
    sc.near = 1;
    sc.far = 90;
    this.applyStyle();
    this.scene.add(this.sun, this.sun.target);
  }

  /**
   * The active art style's tone mapping, shadows and post chain, at the
   * current quality. Its materials are in the meshes, so a style switch also
   * rebuilds the park (`build`) and the rider.
   */
  applyStyle(): void {
    const style = activeStyle();
    this.renderer.toneMapping = style.toneMapping;
    this.renderer.toneMappingExposure = style.exposure;
    const sh = this.sun.shadow;
    const size = shadowMapSize(style.shadows.mapSize, this.quality);
    if (sh.mapSize.x !== size) {
      sh.mapSize.set(size, size);
      // A new size needs a new shadow map; three makes one on the next render.
      sh.map?.dispose();
      sh.map = null;
    }
    sh.radius = style.shadows.radius;
    sh.bias = style.shadows.bias;
    sh.normalBias = style.shadows.normalBias;
    this.composer?.dispose();
    this.composer = null;
    this.passes = activePasses(style.post, this.quality);
    if (this.dust) {
      this.scene.remove(this.dust.points);
      this.dust.dispose();
      this.dust = null;
    }
    if (style.dust && this.quality === "high") {
      this.dust = new Dust(style.dust);
      this.dust.setColor(this.sun.color);
      this.scene.add(this.dust.points);
    }
    this.applyResolution();
  }

  /**
   * The style's drawing resolution (#76): about a fixed number of lines
   * blown up with hard pixel edges, or the screen's own. Never finer than
   * the screen. The lines are the screen's own pixels divided by a whole
   * number (#146), so every pixel comes out the same size: 240 lines on a
   * 1080p screen would be 4.5x, alternate rows 4 and 5 pixels tall, and the
   * pixels shimmer instead of reading as chunky squares.
   */
  private applyResolution(): void {
    const res = activeStyle().resolution;
    const blowUp = res ? Math.max(1, Math.round((this.height * this.deviceRatio) / res.lines)) : 1;
    this.renderer.setPixelRatio(res ? Math.min(this.deviceRatio / blowUp, this.screenRatio) : this.screenRatio);
    this.renderer.setSize(this.width, this.height, false);
    this.canvas.style.imageRendering = res ? "pixelated" : "";
  }

  /** Low or High (#75): Low skips ambient occlusion and bloom and halves the shadow map. */
  setQuality(q: "low" | "high"): void {
    if (q === this.quality) return;
    this.quality = q;
    this.applyStyle();
  }

  /** The map's park, walls, sky, fog and lighting in the active art style, replacing whatever was there. */
  build(level: Level, look: MapLook, hubs: readonly HubSpot[] | undefined): void {
    if (this.world) {
      this.scene.remove(this.world);
      disposeWorld(this.world);
    }
    const style = activeStyle();
    const sky = new THREE.Color(look.sky);
    this.scene.background = sky;
    this.scene.fog = new THREE.Fog(sky, look.fogNear * style.fog.near, look.fogFar * style.fog.far);
    this.hemi.color.set(look.hemiSky);
    this.hemi.groundColor.set(look.hemiGround);
    this.hemi.intensity = look.hemiIntensity * style.lighting.hemi;
    this.sun.color.set(style.lighting.sunColor);
    if (look.sunTint) this.sun.color.multiply(new THREE.Color(look.sunTint));
    this.sun.intensity = look.sunIntensity * style.lighting.sun;
    this.dust?.setColor(this.sun.color);
    const mood = moodFor(look);
    if (mood !== this.mood) {
      this.mood = mood;
      // The look-up table is baked into the composer; the next frame builds one for this mood.
      this.composer?.dispose();
      this.composer = null;
    }
    this.useEnvironment(look);
    const env = style.lighting.envIntensity;
    this.scene.environmentIntensity = look.indoor ? env.indoor : env.outdoor;
    this.world = buildLevelMeshes(level, look, hubs);
    this.ticks = tickers(this.world);
    if (!look.indoor) {
      this.world.add(buildSky(look, this.sun.position));
      if (style.sky.clouds) this.world.add(buildClouds());
    }
    this.scene.add(this.world);
  }

  /** The style's environment for this map: the shared studio one, or one lit by the map's sky. */
  private useEnvironment(look: MapLook): void {
    const kind = look.indoor ? "studio" : activeStyle().lighting.environment;
    const old = this.skyEnv;
    this.skyEnv = null;
    if (kind === "studio") {
      this.studioEnv ??= buildEnvironment(this.renderer, "studio", look, this.sun.position);
      this.scene.environment = this.studioEnv;
    } else {
      this.skyEnv = buildEnvironment(this.renderer, "sky", look, this.sun.position);
      this.scene.environment = this.skyEnv;
    }
    old?.dispose();
  }

  /** Keeps the shadow map centered on (x, z). */
  follow(x: number, z: number, y = 0): void {
    this.sun.position.set(x - 20, y + 35, z + 15);
    this.sun.target.position.set(x, y, z);
  }

  /** Depth of field (#179): sharp `distance` metres from the camera, blurring off it by `k` (0 is off). Only styles with `dof` on High show it. */
  setFocus(distance: number, k: number): void {
    this.focusDistance = distance;
    this.focusK = k;
  }

  resize(w: number, h: number): void {
    this.width = w;
    this.height = h;
    this.applyResolution();
    this.composer?.setSize(w, h);
  }

  /** One frame: straight to the canvas, or through the style's post chain. */
  render(camera: THREE.PerspectiveCamera): void {
    const now = performance.now();
    if (this.ticks.length) {
      const t = now / 1000;
      for (const tick of this.ticks) tick(t);
    }
    if (this.dust) {
      const dt = this.lastFrame < 0 ? 0 : Math.min(0.1, (now - this.lastFrame) / 1000);
      // A box around the camera, from a little under the rider to just over the camera.
      this.dust.update(dt, camera.position.x, camera.position.y - 3.5, camera.position.z);
    }
    this.lastFrame = now;
    const mod = this.passes ? this.loadComposer() : null;
    if (!this.passes || !mod) {
      this.renderer.render(this.scene, camera);
      return;
    }
    this.composer ??= mod.buildComposer(this.renderer, this.scene, camera, this.passes, this.width, this.height, this.mood);
    this.composer.setFocus(this.focusDistance, this.focusK);
    this.composer.render();
  }

  /** The composer module if it's loaded; otherwise starts loading it (once) and returns null. */
  private loadComposer(): ComposerModule | null {
    if (!this.composerModule && !this.composerLoading && performance.now() >= this.composerRetryAt) {
      this.composerLoading = true;
      void this.importComposer().then(
        (m) => {
          this.composerModule = m;
        },
        () => {
          // Offline or a stale deploy: keep drawing straight to the canvas, and try again in a while, not every frame.
          this.composerLoading = false;
          this.composerRetryAt = performance.now() + COMPOSER_RETRY_MS;
        },
      );
    }
    return this.composerModule;
  }

  dispose(): void {
    this.composer?.dispose();
    this.dust?.dispose();
    this.renderer.dispose();
  }
}
